import { IdAtendimento, IdContato, IdGrupo, IdInbox, IdMensagem, IdUsuario } from "../common-types";
import { ehListaEnviadaGravadaComoGenerica } from "@/utils/listaDeOpcoes";
import { ehVideoRecado } from "@/utils/videoRecado";
import { GenericServiceBase } from "../generic-service";
import { aplicarParametros } from "../templates-wpp-meta/meta-template-params";
import type { IMetaParams } from "../templates-wpp-meta/meta-wpp-template.service";

export type URL = string
export type WhizMessageTipo = 'audio'|'TEXTO'|'IMAGEM'|'VIDEO'|'AUDIO'|'CONTATO'|'CONTATOS'|'DOCUMENTO'|'FIGURINHA'|'LOTTIE'|'LOCALIZACAO'|'REACAO'|'LISTA'|'LISTA_RESPOSTA'|'ENQUETE'|'INTERATIVA'|'PIX'|'EVENTO'|'GENERICA'|'TEMPLATE_META'|'CONVITE_GRUPO'|'VISUALIZACAO_UNICA'|'NAO_SUPORTADA'|'LIGACAO'|'PEDIDO_CATALOGO'
export type WhizMessageSituacao = 'ENVIADO'|'ENVIANDO'|'SENT'|'RECEIVED'|'ENTREGUE'|'READ'|'LIDA'|'FAILED'
export type WhizMessageDadosResposta = {
  id           : IdMensagem,
  idExterno    : string,
  texto        : string,
  legenda?     : string,
  url?         : string,
  /** Miniatura (base64 JPEG) da citada quando a mídia dela ainda estava pendente. */
  miniatura?   : string,
  tipo         : WhizMessageTipo
  enviadaPorMim: boolean,
  contato      : { codigo: string, id: IdContato, imagem?: string, nome: string },
  /** Presentes quando a citada é de um GRUPO ("Responder em particular"). */
  idGrupo?     : IdGrupo,
  nomeGrupo?   : string,
}

export type WhizBaseMessage = {
  idExterno    : string
  id           : IdMensagem

  idInbox      : IdInbox
  idUsuario    : IdUsuario
  idContato    : IdContato

  tipo         : WhizMessageTipo
  conteudo     : MessageContent

  dataEnvio    : string
  dataCriacao  : string

  situacao     : WhizMessageSituacao
  enviadaPorMim: boolean
  editada        : boolean
  apagada        : boolean
  ehMediaPerdida?: boolean
  ehRespostaStatus?: boolean
  ehEncaminhamento?: boolean

  dadosResposta?: WhizMessageDadosResposta|null

  cursor : string
}

export type WhizMessageGrupo = WhizBaseMessage & {
  contato?: { id: IdContato, nome: string, imagem: string|null, codigo: string }
  idGrupo: IdGrupo
}

export type WhizMessageAtendimento = WhizBaseMessage & {
  idAtendimento: IdAtendimento,
}

export type MessageContent = {}; // eslint-disable-line

export type ReceivedContentBase = MessageContent & { tipo: string, url: URL, legenda?: string, nome_documento?: string }
export type SentContentBase     = MessageContent & { codigo: string, nome_documento?: string }

export type ReceivedTextContent    = ReceivedContentBase & { texto: string; tipo: 'TEXTO' }
export type SentTextContent        = SentContentBase & { mensagem: string }

export type ReceivedStickerContent = ReceivedContentBase & { tipo: 'FIGURINHA' }
export type SentStickerContent     = SentContentBase & { } // eslint-disable-line

export type ReceivedDocumentContent = ReceivedContentBase & { tipo: 'DOCUMENTO' }
export type SentDocumentContent     = SentContentBase & { document: string }

export type ReceivedAudioContent = ReceivedContentBase & { tipo: 'AUDIO' }
export type SentAudioContent     = SentContentBase & { audio: string }

export type ReceivedVideoContent = ReceivedContentBase & { tipo: 'VIDEO' }
export type SentVideoContent     = SentContentBase & { video: string }

export type ReceivedImageContent = ReceivedContentBase & { tipo: 'IMAGEM' }
export type SentImageContent     = SentContentBase & { imagem: string }

export type ReceivedReactionContent = ReceivedContentBase & { telefone: number|string, tipo:'REACAO', reacao: string, foiEnviada: boolean, idMensagem: string }
export type SentReactionContent = SentContentBase & {  } // eslint-disable-line

export type ReceivedPixContent = ReceivedContentBase & { chave: string, tipo_chave?: string, tipo: 'PIX', titular: string, corpo?: string, valor?: string, moeda?: string }
export type SentPixContent     = SentContentBase & {  } // eslint-disable-line

/**
 * Enquete recebida.
 *
 * `opcoes`, `votos` e `votosContagem` chegam como STRING JSON: `conteudo` é
 * `MAP<TEXT,TEXT>` no Cassandra e nada é desserializado na volta — usar
 * `ParseJsonStringOrReturnAsIs` antes de tocar em qualquer um dos três.
 *
 * `votosContagem` é o que o card consome (votos por índice de opção, na mesma
 * ordem de `opcoes`); `votos` é o mapa votante → voto que o servidor usa para
 * substituir o voto anterior da mesma pessoa. Ambos ausentes numa enquete que
 * ainda não recebeu voto, e em toda enquete anterior a esta feature.
 *
 * `permiteMultiplasRespostas` é `boolean | string` porque é isso que chega: o
 * servidor grava `String(false)` no mapa, então a leitura devolve `"false"` —
 * string truthy. Comparar direto sempre dá "permite múltiplas".
 */
export type ReceivedPollContent = ReceivedContentBase & { tipo: 'ENQUETE', nome: string, permiteMultiplasRespostas: boolean | string, opcoes: { opcao: string }[], votos?: string, votosContagem?: string }
export type SentPollContent     = SentContentBase & {  } // eslint-disable-line

export type ListContentSecao  = { titulo: string, items: { titulo: string, descricao: string, id: string }[] }
export type ListContent       = { tipo: 'LISTA', titulo: string, textoRodape: string, textoBotao: string, descricao: string, secoes: ListContentSecao[] }
export type ListAnswerContent = { tipo: 'LISTA_RESPOSTA', idResposta: string|number, texto: string, idMensagemRespondida?: string|null }

export type InteractiveButton  = { tipo: string, texto: string, codigoCopia?: string, url?: string, telefone?: string, idResposta?: string }
export type InteractiveContent = { corpo: string, rodape?: string|null, botoes: InteractiveButton[], tipo: 'INTERATIVA' }

export type EventContent = { eventMessage: { isCanceled: boolean, name: string, startTime: { low: number, high: number, unsigned: boolean }, extraGuestsAllowed: boolean, isScheduleCall: boolean, hasReminder: boolean, reminderOffsetSec?: { low: number, high: number, unsigned: boolean } } }

export type SingleContactContent = { tipo: 'CONTATO', photo: string|null, nome: string, telefone: string }
export type MultipleContactsContent = { tipo: 'CONTATOS', contatos: { photo: string|null, nome: string, telefone: string }[]  }

export type LocalizationContent = { tipo: 'LOCALIZACAO', latitude: string, longitude: string, nome?: string, endereco?: string, url?: string }

export type GroupInviteContent = { tipo: 'CONVITE_GRUPO', nomeGrupo?: string, codigoConvite?: string, jidGrupo?: string, caption?: string }

/**
 * Mensagem que o WhatsApp entregou sem conteúdo porque o tipo dela não é
 * suportado pela Cloud API (`type: "unsupported"`, erro 131051). A mensagem
 * EXISTE na conversa do cliente — o WhatsApp é que não sabe representá-la.
 *
 * `codigoErro`/`motivo` vêm do `errors[0]` do webhook e são opcionais: a Meta
 * não garante `errors[]` em todo `unsupported`.
 */
export type UnsupportedContent = { tipo: 'NAO_SUPORTADA', codigoErro?: number, motivo?: string }

/**
 * Texto do card para um template Meta.
 *
 * O conteúdo guardado é o template SERIALIZADO (`template` e `parametersToReplace` são
 * strings JSON), então nenhum campo de texto está no primeiro nível — era por isso que o
 * card do atendimento ficava em branco depois de mandar um template: o `switch` caía no
 * `default`. A ordem de preferência é o texto do corpo (o que o cliente de fato recebeu),
 * depois o nome de exibição, e só então um rótulo genérico.
 */
function textoDeTemplateMeta(messageContent: MessageContent): string {
    const parse = (valor: unknown): Record<string, unknown> | null => {
        if (valor && typeof valor === 'object') return valor as Record<string, unknown>;
        if (typeof valor !== 'string' || !valor) return null;
        try { return JSON.parse(valor) as Record<string, unknown>; } catch { return null; }
    };

    const template = parse((messageContent as { template?: unknown })?.template);
    const corpo = parse(template?.bodyJson);
    const texto = typeof corpo?.text === 'string' ? corpo.text.trim() : '';

    // Os valores enviados viajam ao lado, em `parametersToReplace` — aplicá-los deixa o
    // card mostrar a frase que o cliente recebeu. Antes os placeholders eram APAGADOS,
    // o que produzia buracos no meio do texto ("Olá, ! Sua consulta com  está...").
    const params = parse((messageContent as { parametersToReplace?: unknown })?.parametersToReplace);
    const substituido = aplicarParametros(texto, params as IMetaParams | null, 'body');

    // O que sobrar sem valor (template antigo, parâmetro faltando) ainda vira ruído na
    // linha de preview, então esses sim saem.
    const limpo = substituido.replace(/\{\{\s*[^}]*?\s*\}\}/g, '').replace(/\s{2,}/g, ' ').trim();

    const nome = typeof template?.displayName === 'string' ? template.displayName : '';
    return limpo || nome || 'Template';
}

/**
 * Conteúdo da prévia de LIGAÇÃO. Não é mensagem: o servidor projeta a ligação
 * encerrada em `ultimaMensagemInfo` para o card da lista poder dizer que houve
 * uma chamada — antes disso a conversa subia para o topo mostrando a última
 * mensagem trocada, e só abrindo o chat dava para saber da ligação.
 */
export type DadosLigacao = {
    direcao?: 'RECEBIDA'|'REALIZADA';
    situacao?: 'TOCANDO'|'ATENDIDA'|'PERDIDA'|'RECUSADA'|'ENCERRADA';
    motivo?: string | null;
    duracaoSegundos?: number | null;
    ehVideo?: boolean;
};

/**
 * O servidor grava o tipo como `GENERICA` com o invólucro `ligacao` no conteúdo
 * — não um valor novo de `TipoMensagem` —, porque durante o rollout os pods da
 * versão anterior quebram ao LER um enum que o client deles não conhece. Mesmo
 * padrão do evento de agenda (`eventMessage`): o tipo real é promovido aqui, na
 * exibição.
 */
export type CallContent = MessageContent & { ligacao?: DadosLigacao } & DadosLigacao;

/** Desembrulha o `ligacao`, aceitando também o objeto já plano (caminho ao vivo). */
export function dadosDeLigacao(content?: CallContent | null): DadosLigacao {
    return (content?.ligacao ?? content ?? {}) as DadosLigacao;
}

/** `125` → `2:05`. */
function duracaoDeChamada(segundos: number): string {
    return `${Math.floor(segundos / 60)}:${String(segundos % 60).padStart(2, '0')}`;
}

/**
 * Frase da ligação no card. Espelha `previa-ligacao.util.ts` do servidor de
 * propósito: a mesma regra nos dois lados evita a lista mostrar uma coisa antes
 * do refetch e outra depois. O texto é recalculado aqui, e não lido de
 * `ultimaMensagem`, porque é assim que TODO tipo de prévia funciona no front.
 */
export function textoDeLigacao(content: CallContent): string {
    const dados = dadosDeLigacao(content);
    const substantivo = dados?.ehVideo ? 'Chamada de vídeo' : 'Chamada de voz';
    const duracao = dados?.duracaoSegundos ?? 0;

    if (duracao > 0) return `${substantivo} · ${duracaoDeChamada(duracao)}`;
    // O celular pareado atendeu: alguém falou com o cliente, só não pelo whiz.
    if (dados?.motivo === 'accepted_elsewhere') return `${substantivo} atendida em outro dispositivo`;
    if (dados?.direcao === 'REALIZADA') return `${substantivo} não atendida`;
    if (dados?.situacao === 'RECUSADA') return `${substantivo} recusada`;
    return `${substantivo} perdida`;
}

/**
 * Rótulo de prévia quando a mídia veio de uma publicação do Instagram
 * compartilhada. `compartilhamento` chega como string JSON (MAP<TEXT,TEXT> do
 * Cassandra) ou objeto (evento ao vivo) — aqui basta saber se existe e a origem.
 */
function textoDePublicacaoCompartilhada(messageContent: MessageContent): string | undefined {
    const bruto = (messageContent as { compartilhamento?: unknown })?.compartilhamento;
    if (!bruto) return undefined;
    const ehReel = typeof bruto === 'string'
        ? bruto.includes('"ig_reel"')
        : (bruto as { origem?: string })?.origem === 'ig_reel';
    return ehReel ? 'Reel do Instagram' : 'Publicação do Instagram';
}

/**
 * Prévia de um pedido do catálogo do WhatsApp para a LISTA de conversas.
 *
 * O `orderMessage` chega como string JSON (a coluna `conteudo` é MAP<TEXT,TEXT>
 * no Cassandra). Aqui só interessa o título, então basta um parse tolerante:
 * pedido ilegível ainda vale a prévia genérica, nunca string vazia — que
 * deixaria a conversa sem última mensagem.
 */
export function textoDePedidoCatalogo(conteudo: MessageContent | undefined): string | undefined {
    const c = conteudo as { orderMessage?: unknown; order?: unknown } | undefined;
    const bruto = c?.orderMessage ?? c?.order;
    if (!bruto) return undefined;
    try {
        const pedido = (typeof bruto === 'string' ? JSON.parse(bruto) : bruto) as {
            orderTitle?: string;
            product_items?: unknown[];
        };
        // Transporte baileys: tem nome de produto.
        if (pedido?.orderTitle) return `Pedido: ${pedido.orderTitle}`;
        // Transporte oficial da Meta: NÃO tem nome nenhum, só SKU — a contagem
        // de itens é a informação mais útil que cabe numa linha de prévia.
        const quantos = Array.isArray(pedido?.product_items) ? pedido.product_items.length : 0;
        if (quantos > 0) return `Pedido do catálogo (${quantos} ${quantos === 1 ? 'item' : 'itens'})`;
    } catch {
        // Pedido ilegível ainda vale a prévia genérica abaixo.
    }
    return 'Pedido do catálogo';
}

export function getDisplayTextForMessageContent(messageContent: MessageContent, messageType?: WhizMessageTipo, reactionAuthor?: string): string {
    switch (messageType) {
      case "TEMPLATE_META" : return textoDeTemplateMeta(messageContent);
      case "audio"         : return 'Áudio';
      case "AUDIO"         : return 'Áudio';
      case "TEXTO"         : return (messageContent as ReceivedTextContent)?.url || (messageContent as SentTextContent)?.mensagem || (messageContent as {texto?: string})?.texto || 'Texto';
      // Publicação do Instagram compartilhada CHEGA com mídia (o `ig_post` traz a
      // CDN da imagem), então o tipo é IMAGEM/VIDEO. Dizer só "Imagem" na prévia
      // apaga a única pista de que veio de uma publicação.
      case "IMAGEM"        : return textoDePublicacaoCompartilhada(messageContent) ?? 'Imagem';
      case "VIDEO"         : return textoDePublicacaoCompartilhada(messageContent) ?? 'Vídeo';
      case "CONTATO"       : return 'Contato';
      case "CONTATOS"      : return 'Contatos';
      case "DOCUMENTO"     : return 'Documento';
      case "FIGURINHA"     : return 'Figurinha';
      case "LOTTIE"        : return 'Imagem (vetor)';
      case "LOCALIZACAO"   : return 'Localização';
      case "REACAO"        : return `${reactionAuthor ? `${reactionAuthor} reagiu com ` : ''} ${(messageContent as ReceivedReactionContent)?.reacao || 'Reação'}` ; // || (messageContent as SentReactionContent)?.reacao
      case "LISTA"         : return (messageContent as ListContent)?.titulo || 'Lista';
      case "LISTA_RESPOSTA": return (messageContent as ListAnswerContent)?.texto || 'Resposta a Lista';
      case "ENQUETE"       : return 'Enquete';
      case "INTERATIVA"    : return 'Botão';
      case "PIX"           : return 'Pix';
      case "EVENTO"        : return 'Evento';
      // Evento de agenda ainda chega classificado como GENERICA pelo servidor;
      // o invólucro `eventMessage` no conteúdo denuncia o tipo real. Mesma coisa
      // para a publicação compartilhada do Instagram (`compartilhamento`), que
      // sem isto aparecia como "Genérica" na prévia da conversa.
      case "PEDIDO_CATALOGO": return textoDePedidoCatalogo(messageContent) ?? 'Pedido do catálogo';
      // O pedido do catálogo (`orderMessage`) entra na mesma lista de exceções:
      // esta função é chamada com o tipo CRU do servidor, então o histórico
      // gravado como GENERICA só escapa de "Genérica" aqui.
      case "GENERICA"      : return (messageContent as { ligacao?: unknown })?.ligacao
                                      ? textoDeLigacao(messageContent as CallContent)
                                      : (messageContent as { compartilhamento?: unknown })?.compartilhamento
                                      ? 'Publicação do Instagram'
                                      : ((messageContent as { orderMessage?: unknown })?.orderMessage
                                         || (messageContent as { order?: unknown })?.order)
                                      ? textoDePedidoCatalogo(messageContent) ?? 'Pedido do catálogo'
                                      : (messageContent as { eventMessage?: unknown })?.eventMessage ? 'Evento'
                                      : ehVideoRecado(messageContent) ? 'Vídeo'
                                      // Lista enviada regravada como GENERICA pelo status da Meta.
                                      : ehListaEnviadaGravadaComoGenerica(messageContent) ? ((messageContent as ListContent)?.titulo || 'Lista')
                                      : 'Genérica';
      case "LIGACAO"       : return textoDeLigacao(messageContent as CallContent);
      case "VISUALIZACAO_UNICA": return 'Visualização única';
      case "NAO_SUPORTADA" : return 'Mensagem não suportada';
      case "CONVITE_GRUPO" : return (messageContent as GroupInviteContent)?.nomeGrupo ? `Convite: ${(messageContent as GroupInviteContent).nomeGrupo}` : 'Convite para grupo';
      default: return ''
    }
}

export function unwrapMessageContentPayload(messageContent: MessageContent, messageType: WhizMessageTipo): string {
  console.log('[unwrapMessageContentPayload] content e tipo', messageContent, messageType);
    switch (messageType) {
      case "audio"         : return (messageContent as ReceivedAudioContent)?.url || (messageContent as SentAudioContent)?.audio || 'Áudio';
      case "AUDIO"         : return (messageContent as ReceivedAudioContent)?.url || (messageContent as SentAudioContent)?.audio || 'Áudio';
      case "TEXTO"         : return (messageContent as ReceivedTextContent)?.url || (messageContent as SentTextContent)?.mensagem || (messageContent as {texto?: string})?.texto || 'Texto';
      case "IMAGEM"        : return (messageContent as ReceivedImageContent)?.url || (messageContent as SentImageContent)?.imagem || 'Imagem';
      case "VIDEO"         : return (messageContent as ReceivedVideoContent)?.url || (messageContent as SentVideoContent)?.video || 'Vídeo';
      case "CONTATO"       : return 'Contato';
      case "CONTATOS"      : return 'Contatos';
      case "DOCUMENTO"     : return (messageContent as ReceivedDocumentContent)?.url || (messageContent as SentDocumentContent)?.document || 'Documento';
      case "FIGURINHA"     : return (messageContent as ReceivedStickerContent)?.url || 'Figurinha'; // || (messageContent as SentStickerContent)?.sticker
      case "LOTTIE"        : return (messageContent as ReceivedImageContent)?.url || (messageContent as SentImageContent)?.imagem || 'Imagem (vetor)';
      case "LOCALIZACAO"   : return 'Localização';
      case "REACAO"        : return (messageContent as ReceivedReactionContent)?.reacao || 'Reação'; // || (messageContent as SentReactionContent)?.reacao
      case "LISTA"         : return (messageContent as ListContent)?.titulo || 'Lista';
      case "LISTA_RESPOSTA": return (messageContent as ListAnswerContent)?.texto || 'Resposta a Lista';
      case "ENQUETE"       : return 'Enquete';
      case "INTERATIVA"    : return 'Botão';
      case "PIX"           : return 'Pix';
      case "EVENTO"        : return 'Evento';
      case "PEDIDO_CATALOGO": return textoDePedidoCatalogo(messageContent) ?? 'Pedido do catálogo';
      case "GENERICA"      : return (messageContent as { compartilhamento?: unknown })?.compartilhamento ? 'Publicação do Instagram'
                                  : ((messageContent as { orderMessage?: unknown })?.orderMessage
                                     || (messageContent as { order?: unknown })?.order) ? textoDePedidoCatalogo(messageContent) ?? 'Pedido do catálogo'
                                  : ehListaEnviadaGravadaComoGenerica(messageContent) ? ((messageContent as ListContent)?.titulo || 'Lista')
                                  : 'Genérica';
      case "VISUALIZACAO_UNICA": return 'Visualização única';
      case "NAO_SUPORTADA" : return 'Mensagem não suportada';
      case "CONVITE_GRUPO" : return (messageContent as GroupInviteContent)?.nomeGrupo || 'Convite para grupo';
      default: return ''
    }
}

/**
 * Classe do ícone do tipo da mensagem, para a prévia na lista de conversas.
 * Devolve a CLASSE e não a tag pronta: quem a usa monta `<i :class>` e o texto
 * da prévia vai por `{{ }}`. Concatenar tag + texto num `v-html` executava
 * HTML vindo do contato.
 */
export function getIconClassFromType(messageType?: WhizMessageTipo|null): string {
    switch (messageType) {
      case "audio"         : return 'ri-music-2-line me-1'; // || '🎵';
      case "AUDIO"         : return 'ri-music-2-line me-1'; // || '🎵';
      case "TEXTO"         : return '';//'<i class="ri-text me-1"></i>'; // || '💬';
      case "IMAGEM"        : return 'ri-image-line me-1'; // || '🖼️';
      case "VIDEO"         : return 'ri-video-line me-1'; // || '📼';
      case "CONTATO"       : return 'ri-user-line me-1'; // || '🪪';
      case "CONTATOS"      : return 'ri-user-line me-1'; // || '🪪';
      case "DOCUMENTO"     : return 'ri-file-text-line me-1'; // || '📄';
      case "FIGURINHA"     : return 'ri-emoji-sticker-line me-1'; // || '💟';
      case "LOTTIE"        : return 'ri-shape-2-line me-1'; // || '🖼️';
      case "LOCALIZACAO"   : return 'ri-map-pin-2-line me-1'; // || '📍';
      case "REACAO"        : return 'ri-emotion-line me-1'; // || '😀';
      case "LISTA"         : return 'ri-list-check me-1'; // || '📋';
      case "LISTA_RESPOSTA": return 'ri-list-check-3 me-1'; // || '📋';
      case "ENQUETE"       : return 'ri-chat-poll-line me-1'; // || '📊';
      case "INTERATIVA"    : return 'ri-cursor-hand me-1'; // || '↗️';
      case "PIX"           : return 'ri-pix-line me-1'; // || '💸';
      case "EVENTO"        : return 'ri-calendar-event-line me-1'; // || '❓';
      case "PEDIDO_CATALOGO": return 'ri-shopping-bag-3-line me-1';
      case "GENERICA"      : return 'ri-question-line me-1'; // || '❔';
      case "LIGACAO"       : return 'ri-phone-line me-1';
      case "VISUALIZACAO_UNICA": return 'ri-eye-off-line me-1'; // || '🔒';
      case "NAO_SUPORTADA" : return 'ri-error-warning-line me-1';
      case "CONVITE_GRUPO" : return 'ri-group-line me-1';
      default: return ''
    }
}

/**
 * "Responder em particular": cita, na conversa 1:1, uma mensagem do grupo. Ids
 * INTERNOS — o servidor resolve o `idExterno`, o JID do grupo e o autor. Nunca vai
 * junto com `respostaPara` (o servidor responde 400).
 */
export type RespostaEmParticularEnvio = {
    idGrupo   : IdGrupo,
    idMensagem: IdMensagem,
}

/**
 * Retorno dos envios. `respostaVinculada` diz se a citação (`respostaPara` ou
 * `respostaEmParticular`) foi anexada ao envio; servidor anterior a ela não o devolve.
 */
export type RespostaEnvioMensagem = {
    idMensagem         : string,
    idExterno?         : string,
    respostaVinculada? : boolean,
}

export type BaseSentMessage = {
    idInbox     : IdInbox,
    codigo      : string,
    grupo       : boolean,
    respostaPara?: string,
    respostaEmParticular?: RespostaEmParticularEnvio,
    mencoes?: string[],
    /** Id da mensagem FALHOU sendo reenviada: o backend reaproveita a mesma linha. */
    idMensagemReenvio?: string,
    /** Marca a mensagem como encaminhada — o servidor persiste e o front exibe o rótulo "Encaminhada". */
    ehEncaminhamento?: boolean,
}

export type SentTextMessage = BaseSentMessage & {
  mensagem            : string,
  substituirVariaveis?: boolean,
}

export type SentImageMessage = BaseSentMessage & {
  imagem: URL,
  legenda?: string,
}

export type SentVideoMessage = BaseSentMessage & {
  video: URL,
  legenda?: string,
}

export type SentDocumentMessage = BaseSentMessage & {
  documento: URL,
  legenda?: string,
  nome_documento?: string,
}

export type SentAudioMessage = BaseSentMessage & {
  audio: URL,
  /** Mimetype real do áudio enviado. Plataformas Meta exigem 'audio/mp4'. */
  mimeType?: string,
}

export type SentStickerMessage = BaseSentMessage & {
  figurinha: URL,
}

export type SentOptionListMessage = BaseSentMessage & {
  mensagem: string,
  lista: {
    titulo: string,
    rotuloBotao: string,
    opcoes: {
      id?: string,
      titulo: string,
      descricao: string
    }[]
  }
}

type MessageCopyBtnDescriptor = {
  tipo        : "copy"
  texto      : string
  codigoCopia: string
}

type MessagePixBtnDescriptor = {
  tipo        : "pix"
  chave      : string
  tipoChave  : string
  nomeTitular: string
}

type MessageButtonDescriptor = MessageCopyBtnDescriptor | MessagePixBtnDescriptor

type BaseSentButtonMessage = BaseSentMessage & {
  titulo?: string
  descricao?: string
  rodape?: string
  botoes: MessageButtonDescriptor[]
}

export type SentPixMessage = BaseSentButtonMessage & { botoes: MessagePixBtnDescriptor[] }

export type SentCopyBtnMessage = BaseSentButtonMessage & { botoes: MessageCopyBtnDescriptor[] }

type MessageContactDescriptor = {
  nomeCompleto: string
  telefone    : string
  codigo      : string
  email       ?: string
  organizacao ?: string
  url         ?: string
}

export type SentSingleContactMessage = BaseSentMessage & { contatos: [MessageContactDescriptor] }

export type SentManyContactsMessage = BaseSentMessage & { contatos: MessageContactDescriptor[] }

export type ResponseMessage = {
  items: WhizMessageAtendimento[],
  pagingState: string | null,
}

export type AltResponseMessage = {
  mensagens: WhizMessageAtendimento[],
  cursor: string | null,
}

export class MensagensService extends GenericServiceBase {
  constructor() { super('mensagens') }

  async enviarTexto(msg: SentTextMessage): Promise<RespostaEnvioMensagem> {
    return await this.f.post('/enviar-texto', msg) as RespostaEnvioMensagem;
  }

  /** Aceita payload em base64 ou o link apropriado */
  async enviarImagem(msg: SentImageMessage): Promise<RespostaEnvioMensagem> {
    return await this.f.post('/enviar-imagem', msg) as RespostaEnvioMensagem;
  }

  async enviarVideo(msg: SentVideoMessage): Promise<RespostaEnvioMensagem> {
    return await this.f.post('/enviar-video', msg) as RespostaEnvioMensagem;
  }

  async enviarDocumento(msg: SentDocumentMessage): Promise<RespostaEnvioMensagem> {
    return await this.f.post('/enviar-documento', msg) as RespostaEnvioMensagem;
  }

  async enviarAudio(msg: SentAudioMessage): Promise<RespostaEnvioMensagem> {
    return await this.f.post('/enviar-audio', msg) as RespostaEnvioMensagem;
  }

  async enviarFigurinha(msg: SentStickerMessage): Promise<RespostaEnvioMensagem> {
    return await this.f.post('/enviar-figurinha', msg) as RespostaEnvioMensagem;
  }

  async enviarListaOpcoes(msg: SentOptionListMessage): Promise<{idMensagem: string}> {
    return await this.f.post('/enviar-lista-de-opcoes', msg) as {idMensagem: string};
  }

  async enviarPix(msg : SentPixMessage): Promise<RespostaEnvioMensagem> {
    return await this.f.post('/enviar-botao', msg) as RespostaEnvioMensagem;
  }

  async enviarSenhaUsoUnico(msg: SentCopyBtnMessage): Promise<RespostaEnvioMensagem> {
    return await this.f.post('/enviar-botao', msg) as RespostaEnvioMensagem;
  }

  async enviarContatos(msg: SentSingleContactMessage | SentManyContactsMessage): Promise<RespostaEnvioMensagem> {
    return await this.f.post('/enviar-contato', msg) as RespostaEnvioMensagem;
  }

  async getMensagensByAtendimento(idAtendimento: IdAtendimento, page = 1, pageSize = 50, abtSignal?: AbortSignal): Promise<ResponseMessage> {
    return await this.f.get(`/atendimento/${idAtendimento}`, {page, pageSize}, {abtSignal}) as ResponseMessage;
  }

  /**
   * Dado um id de grupo, puxa as últimas 50 mensagens.
   *
   * @param idGrupo ID do grupo em questão
   * @param cursor default: `undefined`;
   *
   * O cursor é a âncora que determina o ponto de referência a partir do qual contar 50 mensagens para trás. `undefined` significa "as 50 mais recentes"; `string` significa "a partir da mensagem com esse cursor, as 50 mais recentes".
   *
   * @param quantidade default: 50
   * @returns Um objeto contendo a página, mais a quantidade total de mensagens.
   */
  async getMensagensByGrupo(idGrupo: IdGrupo, cursor?: string|null, quantidade = 50, abtSignal?: AbortSignal) {
    const response = await this.f.get(`/grupo/${idGrupo}`, {quantidade, cursor}, {abtSignal}) as AltResponseMessage;
    return {
      items: response.mensagens,
      pagingState: response.cursor
    } as ResponseMessage
  }

  async getMensagensCrossAtendimentoByInboxContato(idContato: IdContato, idInbox: IdInbox, cursor?: string|null, quantidade: number = 50) {
    const response = await this.f.get(`/inboxes/${idInbox}/contatos/${idContato}`, {cursor, quantidade}) as AltResponseMessage;
    return {
      items: response.mensagens,
      pagingState: response.cursor
    } as ResponseMessage
  }

  //* Os editares só funcionam para mensagens com menos de 1h, mas não retornam erro otherwise
  /** Edita mensagens de texto (o mesmo endpoint edita legenda de imagens enviadas pela evolution) */
  async editarMensagemTexto(idInternoMensagem: IdMensagem, novoTexto: string) {
    return await this.f.put(`/editar-texto`, { idMensagem: idInternoMensagem, novoTexto });
  }

  /** Só existe para inboxes da ZAPI */
  async editarLegendaDocumentoZAPI(idInternoMensagem: IdMensagem, novoTexto: string) {
    return await this.f.put(`/editar-legenda-documento`, { idMensagem: idInternoMensagem, novoTexto });
  }

  /** Só existe para inboxes da Evolution */
  async editarLegendaImagemEVO(idInternoMensagem: IdMensagem, novaLegenda: string) {
    return await this.f.put(`/editar-legenda`, { idMensagem: idInternoMensagem, novaLegenda });
  }

  /** Só funciona para mensagens com menos de 24h, mas não retorna erro otherwise; Funciona para a ZAPI; por ora bugado na Evo; não existe na Meta. Só funciona para mensagens próprias. */
  async deletarMensagem(idInternoMensagem: IdMensagem, telefone: string) {
    return await this.f.delete(`/messages`, { messageId: idInternoMensagem, phone: telefone, owner: true });
  }

  async enviarReacao(msg: { idMensagem?: IdMensagem; idExterno?: string; fromMe?: boolean; reacao: string }): Promise<{ status: string; mensagem: string }> {
    return await this.f.post('/enviar-reacao', msg) as { status: string; mensagem: string };
  }

  /** Exporta toda a conversa (todos os atendimentos do contato no inbox). Restrito a usuários com acesso global no backend. */
  async exportarConversa(idInbox: IdInbox, idContato: IdContato): Promise<ExportarConversaResponse> {
    return await this.f.get(`/inboxes/${idInbox}/contatos/${idContato}/exportar`) as ExportarConversaResponse;
  }
}

export type ExportarConversaResponse = {
  contato: { id: IdContato; nome: string; codigo: string };
  inbox: { id: IdInbox; nome: string };
  atendentes: { id: IdUsuario; nome: string }[];
  total: number;
  geradoEm: string;
  mensagens: WhizMessageAtendimento[];
}
